/**
 * Der laufende Test einer Person — beobachtet von der Anwendung, nicht von der
 * Seite, auf der er gestartet wurde.
 *
 * Bis zum 16.09.2026 hing das Abfragen des Fortschritts an `SimulatePage`:
 * Wer die Seite verliess, dem wurde das Intervall abgeräumt, und kam er zurück,
 * stand dort ein leeres Formular mit einem Start-Knopf — obwohl der Test im
 * Worker weiterlief. Einen zweiten konnte er nicht starten (ein Lauf je
 * Person, `acquire_sweep_slot`), sah aber nicht, warum. Und das Ergebnis
 * bekam er nur mit, wenn er zufällig unter „Ergebnisse" nachsah.
 *
 * Jetzt fragt der Provider beim Anmelden EINMAL nach; steht ein Lauf an, hängt
 * er sich an und fragt weiter, bis er endet. Den Endzustand hält er fest, bis
 * jemand ihn quittiert (`vergessen`) — die Simulationsseite wechselt dann zum
 * Ergebnis, die Hinweisleiste (`LaufHinweis`) sagt es auf jeder anderen Seite.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { components } from '../types/api.generated';
import { simulationAPI } from '../services/api';
import { useAuth } from './useAuth';

export type SweepFortschritt = components['schemas']['SweepFortschritt'];

/** Zustände, in denen noch gerechnet wird — wie `LAUFEND` im Backend. */
const LAUFEND: ReadonlySet<string> = new Set(['preparing', 'running', 'cancelling']);

export function istLaufend(lauf: SweepFortschritt | null): boolean {
  return !!lauf && LAUFEND.has(lauf.status);
}

const ABFRAGE_MS = 1000;
/** Ab so vielen fehlgeschlagenen Abfragen in Folge sagt die Oberfläche es. */
const FEHLER_BIS_HINWEIS = 3;

interface Wert {
  /** Letzter bekannter Stand — `null`, solange kein Lauf beobachtet wird. */
  lauf: SweepFortschritt | null;
  /** Die Abfrage scheitert gerade wiederholt (der Lauf kann weiterlaufen). */
  verbindungGestoert: boolean;
  /** Nach einem erfolgreichen Start aufrufen. */
  beobachte: () => void;
  /** Wirft bei einem Fehler — der Aufrufer zeigt ihn. */
  abbrechen: () => Promise<void>;
  /** Endzustand gesehen: nicht mehr anzeigen. */
  vergessen: () => void;
}

const LaufKontext = createContext<Wert | null>(null);

export function SimulationsLaufProvider({ children }: { children: ReactNode }) {
  // Erst nach `/me`: Vorher steht in `user` der Cache aus dem localStorage —
  // das zuletzt angemeldete Konto, das nicht das des Cookies sein muss.
  const { sitzungsNutzer } = useAuth() as { sitzungsNutzer: { id: number } | null };
  const nutzerId = sitzungsNutzer?.id ?? null;
  const [lauf, setLauf] = useState<SweepFortschritt | null>(null);
  const [fehlschlaege, setFehlschlaege] = useState(0);
  const [aktiv, setAktiv] = useState(false);
  // Die Antwort einer Abfrage, die für einen ANDEREN Nutzer lief (Abmelden,
  // Mandantenwechsel), darf den Stand nicht mehr setzen.
  const fuerNutzer = useRef<number | null>(nutzerId);
  fuerNutzer.current = nutzerId;

  const abfragen = useCallback(async (id: number): Promise<SweepFortschritt | null> => {
    try {
      const { data } = await simulationAPI.getProgress();
      if (fuerNutzer.current !== id) return null;
      setFehlschlaege(0);
      return data as SweepFortschritt;
    } catch (err) {
      console.error('[Simulation] Fortschritt nicht abrufbar', err);
      setFehlschlaege((n) => n + 1);
      return null;
    }
  }, []);

  // Beim Anmelden (und bei jedem Wechsel des Kontos): läuft schon etwas?
  useEffect(() => {
    setLauf(null);
    setAktiv(false);
    if (nutzerId === null) return;
    let abgebrochen = false;
    abfragen(nutzerId).then((stand) => {
      if (abgebrochen || !stand || !LAUFEND.has(stand.status)) return;
      setLauf(stand);
      setAktiv(true);
    });
    return () => { abgebrochen = true; };
  }, [nutzerId, abfragen]);

  // Solange ein Lauf aktiv ist: weiter fragen, bis er endet.
  useEffect(() => {
    if (!aktiv || nutzerId === null) return;
    let zeitgeber: ReturnType<typeof setTimeout>;
    let beendet = false;
    const runde = async () => {
      const stand = await abfragen(nutzerId);
      if (beendet) return;
      if (stand) {
        setLauf(stand);
        if (!LAUFEND.has(stand.status)) {
          setAktiv(false);
          return;
        }
      }
      zeitgeber = setTimeout(runde, ABFRAGE_MS);
    };
    zeitgeber = setTimeout(runde, ABFRAGE_MS);
    return () => { beendet = true; clearTimeout(zeitgeber); };
  }, [aktiv, nutzerId, abfragen]);

  const beobachte = useCallback(() => {
    setLauf({ status: 'preparing', phase: 'preparing', percent: 0 });
    setFehlschlaege(0);
    setAktiv(true);
  }, []);

  const abbrechen = useCallback(async () => {
    const { data } = await simulationAPI.cancel();
    setLauf(data as SweepFortschritt);
  }, []);

  const vergessen = useCallback(() => {
    setLauf((jetzt) => (istLaufend(jetzt) ? jetzt : null));
  }, []);

  const wert = useMemo<Wert>(() => ({
    lauf,
    verbindungGestoert: aktiv && fehlschlaege >= FEHLER_BIS_HINWEIS,
    beobachte,
    abbrechen,
    vergessen,
  }), [lauf, aktiv, fehlschlaege, beobachte, abbrechen, vergessen]);

  return <LaufKontext.Provider value={wert}>{children}</LaufKontext.Provider>;
}

export function useSimulationsLauf(): Wert {
  const wert = useContext(LaufKontext);
  if (!wert) throw new Error('useSimulationsLauf braucht den SimulationsLaufProvider');
  return wert;
}
